import { ComponentProps, FC, InputHTMLAttributes, PropsWithChildren } from "react";
import { DropzoneOptions, DropzoneState, useDropzone } from "react-dropzone";

import { cn } from "../utils";
import { Icon, IconProps } from "./icon";

export interface FileDropzoneProps
  extends Omit<DropzoneOptions, "multiple" | "onDragEnter" | "onDragLeave" | "onDragOver"> {
  className?: string;
  overlayClassName?: string;
  multiple?: DropzoneOptions["multiple"];
  onDragEnter?: DropzoneOptions["onDragEnter"];
  onDragLeave?: DropzoneOptions["onDragLeave"];
  onDragOver?: DropzoneOptions["onDragOver"];
}

export const FileDropzone: FC<PropsWithChildren<FileDropzoneProps>> = ({
  children,
  className,
  overlayClassName,
  ...dropzoneOptions
}) => {
  const {
    multiple = true,
    onDragEnter = undefined,
    onDragLeave = undefined,
    onDragOver = undefined,
    ...restDropzoneOptions
  } = dropzoneOptions;

  const dropzoneState = useDropzone({
    noClick: true,
    multiple,
    onDragEnter,
    onDragLeave,
    onDragOver,
    ...restDropzoneOptions,
  });

  return (
    <FileDropzoneWrapper dropzoneState={dropzoneState} className={className}>
      {children}
      <FileDropzoneOverlay dropzoneState={dropzoneState} className={overlayClassName} />
    </FileDropzoneWrapper>
  );
};

export interface FileDropzoneWrapperProps extends ComponentProps<"div"> {
  dropzoneState: DropzoneState;
}

export const FileDropzoneWrapper: FC<FileDropzoneWrapperProps> = ({ children, className, dropzoneState, ...props }) => (
  <div
    {...dropzoneState.getRootProps()}
    {...props}
    tabIndex={!dropzoneState.isDragActive ? undefined : -1}
    className={cn("relative", className)}
  >
    {children}
    <input {...(dropzoneState.getInputProps() as InputHTMLAttributes<HTMLInputElement>)} />
  </div>
);

export interface FileDropzoneOverlayProps {
  dropzoneState: DropzoneState;
  icon?: IconProps["icon"];
  placeholder?: string;
  className?: string;
}

export const FileDropzoneOverlay: FC<FileDropzoneOverlayProps> = ({
  dropzoneState,
  icon = "cloud_upload",
  placeholder = "Drop file to upload",
  className,
}) => (
  <div
    className={cn(
      "absolute inset-0 rounded-md border border-dashed border-muted-foreground/50 bg-background/90 flex gap-2 items-center justify-center text-muted-foreground transition-all duration-300",
      {
        "opacity-100 visible": dropzoneState.isDragActive,
        "opacity-0 invisible": !dropzoneState.isDragActive,
      },
      className
    )}
  >
    {icon && <Icon icon={icon} className="text-2xl" />}
    {placeholder}
  </div>
);
